/**
 * Deep Linking Utilities
 * Handles app opening when links are clicked on mobile devices
 * Supports: PWA, iOS Universal Links, Android App Links (Flutter)
 * 
 * SECURITY: Comprehensive security measures implemented:
 * - URL validation and sanitization
 * - Open redirect prevention
 * - Path whitelisting
 * - Query parameter validation
 * - XSS prevention
 * - Rate limiting
 * - Domain validation
 */

import { sanitizeUrl, sanitizeString } from './sanitization';

// Android package name - Configured for Baakas Buyer app
const ANDROID_PACKAGE_NAME = process.env.REACT_APP_ANDROID_PACKAGE_NAME || 'com.baakas.buyer';
// Android app scheme - Custom URL scheme for deep linking
const ANDROID_APP_SCHEME = process.env.REACT_APP_ANDROID_SCHEME || 'baakas://';

// SECURITY: Allowed domains for deep linking (prevent open redirects)
const ALLOWED_DOMAINS = [
  'baakas.com.np',
  'www.baakas.com.np',
  'localhost', // For development only
  '127.0.0.1', // For development only
];

// SECURITY: Allowed paths for deep linking (whitelist approach)
const ALLOWED_PATHS = [
  '/',
  '/product/',
  '/categories',
  '/category/',
  '/cart',
  '/checkout',
  '/profile',
  '/orders',
  '/order/',
  '/login',
  '/register',
  '/auth/login',
  '/email-verification',
  '/forgot-password',
  '/payment/',
  '/store/',
  '/seller/',
  '/wishlist',
  '/contact',
  '/about',
  '/help',
  // AFFILIATE (disabled for now): '/affiliate',
];

// SECURITY: Maximum URL length to prevent DoS
const MAX_URL_LENGTH = 2048;

// SECURITY: Rate limiting for deep link attempts
const deepLinkAttempts = new Map<string, number[]>();
const MAX_ATTEMPTS = 10;
const RATE_LIMIT_WINDOW_MS = 60000; // 1 minute

// SECURITY: Blocked patterns for additional protection
const BLOCKED_PATTERNS = [
  /\.\./,           // Path traversal
  /\/\//,           // Double slashes
  /%2e%2e/i,        // URL-encoded path traversal
  /%2f/i,           // URL-encoded slashes
  /<script/i,        // Script tags
  /javascript:/i,   // JavaScript protocol
  /data:text\/html/i, // Data URLs
  /vbscript:/i,     // VBScript
  /on\w+\s*=/i,     // Event handlers
  /expression\s*\(/i, // CSS expressions
];

// SECURITY: Sensitive paths that require additional validation and authorization
const SENSITIVE_PATHS = [
  '/checkout',
  '/payment/',
  '/profile',
  '/orders',
  '/order/',
  '/store/',
  '/seller/',
];

// SECURITY: Paths that require authentication (IDOR protection)
const AUTH_REQUIRED_PATHS = [
  '/checkout',
  '/payment/',
  '/profile',
  '/orders',
  '/order/',
  '/wishlist',
  '/cart',
];

// SECURITY: Paths with resource IDs that need ownership verification
const RESOURCE_ID_PATHS = [
  '/order/',      // Order ID - must belong to user
  '/product/',    // Product ID - public but validate format
  '/store/',      // Store ID - public but validate format
  '/seller/',     // Seller ID - public but validate format
];

// SECURITY: Maximum query parameters to prevent DoS
const MAX_QUERY_PARAMS = 20;
const MAX_QUERY_VALUE_LENGTH = 500;
const MAX_QUERY_KEY_LENGTH = 100;

/**
 * SECURITY: Check for blocked patterns in URL
 * Prevents various injection and XSS attacks
 */
function containsBlockedPatterns(input: string): boolean {
  return BLOCKED_PATTERNS.some(pattern => pattern.test(input));
}

/**
 * SECURITY: Validate origin/referer for CSRF protection
 * Ensures deep links come from trusted sources
 */
function validateOrigin(origin?: string | null, referer?: string | null): { valid: boolean; error?: string } {
  // In browser context, we can validate origin
  if (typeof window !== 'undefined') {
    const currentOrigin = window.location.origin;
    const currentHostname = window.location.hostname;
    
    // Allow same-origin requests
    if (origin && origin === currentOrigin) {
      return { valid: true };
    }
    
    // Validate referer if origin is not available
    if (referer) {
      try {
        const refererUrl = new URL(referer);
        const isAllowedDomain = ALLOWED_DOMAINS.some(domain => 
          refererUrl.hostname === domain || refererUrl.hostname.endsWith('.' + domain)
        );
        
        if (isAllowedDomain) {
          return { valid: true };
        }
      } catch {
        // Invalid referer URL
      }
    }
    
    // For deep links opened directly (no origin/referer), allow if from allowed domain
    if (!origin && !referer) {
      const isAllowedDomain = ALLOWED_DOMAINS.some(domain => 
        currentHostname === domain || currentHostname.endsWith('.' + domain)
      );
      
      if (isAllowedDomain) {
        return { valid: true };
      }
    }
  }
  
  // Default: allow (for app contexts where origin may not be available)
  return { valid: true };
}

/**
 * SECURITY: Validate and sanitize URL
 * Prevents open redirects, XSS, CSRF, and other attacks
 */
function validateAndSanitizeUrl(
  url: string, 
  options?: { 
    requireOrigin?: boolean;
    allowSensitivePaths?: boolean;
  }
): { valid: boolean; sanitized?: string; error?: string } {
  if (!url || typeof url !== 'string') {
    return { valid: false, error: 'Invalid URL: must be a string' };
  }

  // SECURITY: Check URL length
  if (url.length > MAX_URL_LENGTH) {
    return { valid: false, error: 'URL too long' };
  }

  // SECURITY: Check for blocked patterns before sanitization
  if (containsBlockedPatterns(url)) {
    return { valid: false, error: 'URL contains blocked patterns' };
  }

  // SECURITY: Sanitize URL
  const sanitized = sanitizeUrl(url, MAX_URL_LENGTH);
  if (!sanitized) {
    return { valid: false, error: 'URL contains invalid characters' };
  }

  // SECURITY: Check sanitized URL for blocked patterns
  if (containsBlockedPatterns(sanitized)) {
    return { valid: false, error: 'URL contains blocked patterns after sanitization' };
  }

  try {
    const urlObj = new URL(sanitized);
    
    // SECURITY: Only allow http and https protocols (prevent protocol downgrade)
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return { valid: false, error: 'Only HTTP and HTTPS protocols are allowed' };
    }

    // SECURITY: Enforce HTTPS in production
    if (process.env.NODE_ENV === 'production' && urlObj.protocol !== 'https:') {
      return { valid: false, error: 'HTTPS required in production' };
    }

    // SECURITY: Validate port (only allow standard ports or no port)
    if (urlObj.port && urlObj.port !== '80' && urlObj.port !== '443' && urlObj.port !== '3000' && urlObj.port !== '5173') {
      // Allow common development ports, block others
      if (process.env.NODE_ENV === 'production') {
        return { valid: false, error: 'Invalid port' };
      }
    }

    // SECURITY: Validate domain (prevent open redirects)
    const hostname = urlObj.hostname.toLowerCase();
    
    // SECURITY: Block IP addresses (except localhost in development)
    if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      if (process.env.NODE_ENV === 'production' || (hostname !== '127.0.0.1' && hostname !== 'localhost')) {
        return { valid: false, error: 'IP addresses not allowed' };
      }
    }

    // SECURITY: Block private/internal IPs and localhost in production
    if (hostname === 'localhost' || 
        hostname === '127.0.0.1' || 
        hostname.startsWith('192.168.') || 
        hostname.startsWith('10.') || 
        hostname.startsWith('172.') ||
        hostname.includes('..') ||
        hostname.includes('@') ||
        hostname.includes(' ')) {
      if (process.env.NODE_ENV === 'production') {
        return { valid: false, error: 'Invalid domain' };
      }
    }

    const isAllowedDomain = ALLOWED_DOMAINS.some(domain => 
      hostname === domain || hostname.endsWith('.' + domain)
    );

    if (!isAllowedDomain) {
      // In production, only allow configured domains
      if (process.env.NODE_ENV === 'production') {
        return { valid: false, error: 'Domain not allowed for deep linking' };
      }
    }

    // SECURITY: Validate path (whitelist approach)
    let path = urlObj.pathname;
    
    // SECURITY: Normalize path (remove double slashes, trailing slashes for comparison)
    path = path.replace(/\/+/g, '/');
    if (path.length > 1 && path.endsWith('/')) {
      path = path.slice(0, -1);
    }

    // SECURITY: Check for path traversal after normalization
    if (path.includes('..') || path.includes('//')) {
      return { valid: false, error: 'Path traversal detected' };
    }

    const isAllowedPath = ALLOWED_PATHS.some(allowedPath => {
      if (allowedPath.endsWith('/')) {
        return path.startsWith(allowedPath);
      }
      return path === allowedPath || path.startsWith(allowedPath + '/');
    });

    if (!isAllowedPath) {
      return { valid: false, error: 'Path not allowed for deep linking' };
    }

    // SECURITY: Check if path is sensitive and requires additional validation
    const isSensitivePath = SENSITIVE_PATHS.some(sensitivePath => 
      path.startsWith(sensitivePath)
    );

    if (isSensitivePath && !options?.allowSensitivePaths) {
      // For sensitive paths, require origin validation
      if (options?.requireOrigin) {
        const originValidation = validateOrigin(
          typeof window !== 'undefined' ? window.location.origin : null,
          typeof document !== 'undefined' ? document.referrer : null
        );
        if (!originValidation.valid) {
          return { valid: false, error: 'Origin validation required for sensitive paths' };
        }
      }
    }

    // SECURITY: Validate query parameters (prevent XSS and DoS)
    const params = urlObj.searchParams;
    
    // SECURITY: Limit number of query parameters
    if (params.toString().split('&').length > MAX_QUERY_PARAMS) {
      return { valid: false, error: 'Too many query parameters' };
    }

    for (const [key, value] of params.entries()) {
      // SECURITY: Check for blocked patterns in keys and values
      if (containsBlockedPatterns(key) || containsBlockedPatterns(value)) {
        return { valid: false, error: 'Query parameters contain blocked patterns' };
      }

      // SECURITY: Validate length
      if (key.length > MAX_QUERY_KEY_LENGTH || value.length > MAX_QUERY_VALUE_LENGTH) {
        return { valid: false, error: 'Query parameter too long' };
      }

      // Sanitize parameter keys and values
      const sanitizedKey = sanitizeString(key, MAX_QUERY_KEY_LENGTH);
      const sanitizedValue = sanitizeString(value, MAX_QUERY_VALUE_LENGTH);
      
      if (!sanitizedKey || !sanitizedValue) {
        return { valid: false, error: 'Invalid query parameters' };
      }
    }

    // SECURITY: Validate fragment/hash (prevent XSS via fragments)
    if (urlObj.hash) {
      const hash = urlObj.hash.substring(1); // Remove #
      if (containsBlockedPatterns(hash)) {
        return { valid: false, error: 'URL fragment contains blocked patterns' };
      }
      // Limit fragment length
      if (hash.length > 500) {
        return { valid: false, error: 'URL fragment too long' };
      }
    }

    return { valid: true, sanitized };
  } catch (error) {
    // SECURITY: If URL parsing fails, check if it's a relative path
    if (sanitized.startsWith('/')) {
      // Validate relative path
      const isAllowedPath = ALLOWED_PATHS.some(allowedPath => {
        if (allowedPath.endsWith('/')) {
          return sanitized.startsWith(allowedPath);
        }
        return sanitized === allowedPath || sanitized.startsWith(allowedPath + '/');
      });

      if (isAllowedPath) {
        // Construct full URL with current origin
        const fullUrl = `${window.location.origin}${sanitized}`;
        return { valid: true, sanitized: fullUrl };
      }
    }
    
    return { valid: false, error: 'Invalid URL format' };
  }
}

/**
 * SECURITY: Rate limiting for deep link attempts
 * Prevents abuse and DoS attacks
 * Uses sliding window algorithm
 */
function checkRateLimit(identifier: string): { allowed: boolean; message?: string; remainingAttempts?: number } {
  const now = Date.now();
  const attempts = deepLinkAttempts.get(identifier) || [];
  
  // Remove old attempts outside the window
  const recentAttempts = attempts.filter(time => now - time < RATE_LIMIT_WINDOW_MS);
  
  if (recentAttempts.length >= MAX_ATTEMPTS) {
    const oldestAttempt = Math.min(...recentAttempts);
    const waitTime = RATE_LIMIT_WINDOW_MS - (now - oldestAttempt);
    return {
      allowed: false,
      message: `Too many deep link attempts. Please wait ${Math.ceil(waitTime / 1000)} seconds.`,
      remainingAttempts: 0
    };
  }
  
  // Add current attempt
  recentAttempts.push(now);
  deepLinkAttempts.set(identifier, recentAttempts);
  
  // Cleanup old entries periodically (every 100 calls)
  if (Math.random() < 0.01) {
    cleanupRateLimitMap();
  }
  
  return { 
    allowed: true, 
    remainingAttempts: MAX_ATTEMPTS - recentAttempts.length 
  };
}

/**
 * SECURITY: Cleanup old rate limit entries
 * Prevents memory leaks
 */
function cleanupRateLimitMap(): void {
  const now = Date.now();
  for (const [key, attempts] of deepLinkAttempts.entries()) {
    const recentAttempts = attempts.filter(time => now - time < RATE_LIMIT_WINDOW_MS);
    if (recentAttempts.length === 0) {
      deepLinkAttempts.delete(key);
    } else {
      deepLinkAttempts.set(key, recentAttempts);
    }
  }
}

/**
 * Check if the app is installed (PWA or native app)
 */
export function isAppInstalled(): boolean {
  // Check if running as PWA (standalone mode)
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  
  // Check if running as PWA on iOS
  if ((window.navigator as any).standalone === true) {
    return true;
  }
  
  // For Android native app, we can't directly detect if installed
  // But we can attempt to open it and fallback to web
  return false;
}

/**
 * Check if device is Android
 */
export function isAndroid(): boolean {
  return /Android/i.test(navigator.userAgent);
}

/**
 * SECURITY: Validate package name format
 * Prevents injection attacks via package name
 */
function validatePackageName(packageName: string): boolean {
  if (!packageName || typeof packageName !== 'string') {
    return false;
  }
  
  // Package name must match Android package naming rules
  // Format: com.example.app (lowercase letters, numbers, dots, underscores)
  const packageNameRegex = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;
  return packageNameRegex.test(packageName) && packageName.length <= 200;
}

/**
 * SECURITY: Validate URL scheme format
 * Prevents injection attacks via custom schemes
 */
function validateUrlScheme(scheme: string): boolean {
  if (!scheme || typeof scheme !== 'string') {
    return false;
  }
  
  // URL scheme must be alphanumeric with optional hyphens
  // Must end with ://
  const schemeRegex = /^[a-z][a-z0-9+-]*:\/\/$/i;
  return schemeRegex.test(scheme) || /^[a-z][a-z0-9+-]*:$/i.test(scheme);
}

/**
 * SECURITY: Audit log for deep link attempts
 * Helps with security monitoring and forensics
 */
function auditLogDeepLink(
  action: string,
  url: string,
  success: boolean,
  error?: string
): void {
  // Deep link audit logging (only in development)
  if (process.env.NODE_ENV === 'development') {
    // Removed debug log to prevent sensitive data exposure (URL may contain tokens/IDs)
  }
  // In production, you could send this to a logging service
}

/**
 * Attempt to open the native Android app using Intent URL
 * Falls back to web if app is not installed
 * SECURITY: Comprehensive validation and sanitization
 */
export function openAppOrWeb(url: string, appScheme?: string): void {
  // SECURITY: Validate and sanitize URL first
  const validation = validateAndSanitizeUrl(url, {
    requireOrigin: false, // Deep links may not have origin
    allowSensitivePaths: false, // Require additional validation for sensitive paths
  });
  
  if (!validation.valid || !validation.sanitized) {
    auditLogDeepLink('openAppOrWeb', url, false, validation.error);
    // Invalid deep link URL
    return;
  }

  const safeUrl = validation.sanitized;
  const scheme = appScheme || ANDROID_APP_SCHEME;
  
  // SECURITY: Validate scheme
  if (appScheme && !validateUrlScheme(appScheme)) {
    auditLogDeepLink('openAppOrWeb', url, false, 'Invalid URL scheme');
    // SECURITY: Removed console.error to prevent information leakage
    return;
  }

  // SECURITY: Rate limiting with identifier based on URL hostname
  const rateLimitKey = typeof window !== 'undefined' 
    ? `deeplink_${window.location.hostname}_${new URL(safeUrl).hostname}`
    : `deeplink_${new URL(safeUrl).hostname}`;
  const rateLimit = checkRateLimit(rateLimitKey);
  if (!rateLimit.allowed) {
    auditLogDeepLink('openAppOrWeb', url, false, rateLimit.message);
    // Deep link rate limit exceeded
    // Block the request if rate limited
    return;
  }

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  if (!isMobile) {
    // Desktop: just navigate normally (URL already validated)
    window.location.href = safeUrl;
    return;
  }
  
  // If already in PWA, just navigate
  if (isAppInstalled()) {
    window.location.href = safeUrl;
    return;
  }
  
  // Android: Use Intent URL to open app
  if (isAndroid()) {
    try {
      const urlObj = new URL(safeUrl);
      const path = urlObj.pathname + urlObj.search;
      
      // SECURITY: Validate package name
      if (!validatePackageName(ANDROID_PACKAGE_NAME)) {
        // SECURITY: Removed console.error to prevent information leakage
        window.location.href = safeUrl;
        return;
      }
      
      // SECURITY: Sanitize hostname for Intent URL
      const sanitizedHost = sanitizeString(urlObj.hostname, 253) || urlObj.hostname;
      const sanitizedPath = sanitizeString(path, 2000) || path;
      
      // Create Android Intent URL with validated inputs
      // Format: intent://hostname/path#Intent;scheme=https;package=com.example.app;end
      const intentUrl = `intent://${sanitizedHost}${sanitizedPath}#Intent;scheme=https;package=${ANDROID_PACKAGE_NAME};S.browser_fallback_url=${encodeURIComponent(safeUrl)};end`;
      
      // SECURITY: Validate Intent URL components one more time
      if (containsBlockedPatterns(intentUrl)) {
        auditLogDeepLink('openAppOrWeb', url, false, 'Intent URL contains blocked patterns');
        // Intent URL contains blocked patterns
        window.location.href = safeUrl;
        return;
      }

      // Try to open app
      auditLogDeepLink('openAppOrWeb', url, true);
      window.location.href = intentUrl;
      
      // Fallback: If app doesn't open within 2 seconds, open in browser
      setTimeout(() => {
        // If we're still here, app didn't open, so open in browser
        if (document.visibilityState === 'visible') {
          window.location.href = safeUrl;
        }
      }, 2000);
    } catch (error) {
      // Error creating Intent URL
      // Fallback to safe URL
      window.location.href = safeUrl;
    }
  } else {
    // iOS or other: Use custom scheme (with validation)
    try {
      const urlObj = new URL(safeUrl);
      const path = urlObj.pathname + urlObj.search;
      const sanitizedPath = sanitizeString(path, 2000) || path;
      const appUrl = `${scheme}${sanitizedPath}`;
      
      window.location.href = appUrl;
      
      // Fallback to web
      setTimeout(() => {
        if (document.visibilityState === 'visible') {
          window.location.href = safeUrl;
        }
      }, 2000);
    } catch (error) {
      // Error creating custom scheme URL
      window.location.href = safeUrl;
    }
  }
}

/**
 * SECURITY: Validate resource ID format
 * Prevents IDOR attacks by validating ID format before processing
 */
function validateResourceId(id: string, resourceType: 'order' | 'product' | 'store' | 'seller'): boolean {
  if (!id || typeof id !== 'string') {
    return false;
  }

  // SECURITY: Validate ID length (Firestore document IDs are typically 20-28 chars)
  if (id.length < 10 || id.length > 128) {
    return false;
  }

  // SECURITY: Validate ID format (alphanumeric, hyphens, underscores only)
  // Firestore document IDs: alphanumeric, hyphens, underscores
  const idRegex = /^[a-zA-Z0-9_-]+$/;
  if (!idRegex.test(id)) {
    return false;
  }

  // SECURITY: Block common attack patterns
  if (containsBlockedPatterns(id)) {
    return false;
  }

  return true;
}

/**
 * SECURITY: Extract and validate resource ID from path
 * Returns null if ID is invalid or path doesn't contain a resource ID
 */
function extractResourceId(path: string): { type: string; id: string } | null {
  for (const resourcePath of RESOURCE_ID_PATHS) {
    if (path.startsWith(resourcePath)) {
      const id = path.substring(resourcePath.length).split('/')[0].split('?')[0];
      const resourceType = resourcePath.replace('/', '').replace('/', '') as 'order' | 'product' | 'store' | 'seller';
      
      if (validateResourceId(id, resourceType)) {
        return { type: resourceType, id };
      }
    }
  }
  return null;
}

/**
 * SECURITY: Check if path requires authentication
 * IDOR Protection - Prevents unauthorized access to sensitive resources
 */
function requiresAuthentication(path: string): boolean {
  return AUTH_REQUIRED_PATHS.some(authPath => {
    if (authPath.endsWith('/')) {
      return path.startsWith(authPath);
    }
    return path === authPath || path.startsWith(authPath + '/');
  });
}

/**
 * SECURITY: Handle deep link routing with validation and IDOR protection
 * Extracts path and query params from deep link URL
 * Validates and sanitizes all inputs
 * Checks authorization for sensitive paths
 */
export function handleDeepLink(
  url: string,
  options?: { 
    currentUser?: { uid: string } | null;
    checkAuth?: boolean;
  }
): { path: string; params: URLSearchParams; requiresAuth?: boolean; resourceId?: { type: string; id: string } | null } {
  // SECURITY: Validate and sanitize URL first
  const validation = validateAndSanitizeUrl(url, {
    requireOrigin: false,
    allowSensitivePaths: false,
  });
  
  if (!validation.valid || !validation.sanitized) {
    // Invalid deep link URL
    return { path: '/', params: new URLSearchParams() };
  }

  try {
    const urlObj = new URL(validation.sanitized);
    let path = urlObj.pathname;
    
    // SECURITY: Normalize path
    path = path.replace(/\/+/g, '/');
    if (path.length > 1 && path.endsWith('/')) {
      path = path.slice(0, -1);
    }

    // SECURITY: Validate path is allowed
    const isAllowedPath = ALLOWED_PATHS.some(allowedPath => {
      if (allowedPath.endsWith('/')) {
        return path.startsWith(allowedPath);
      }
      return path === allowedPath || path.startsWith(allowedPath + '/');
    });

    if (!isAllowedPath) {
      // Deep link path not allowed
      return { path: '/', params: new URLSearchParams() };
    }

    // SECURITY: IDOR Protection - Check if path requires authentication
    const needsAuth = requiresAuthentication(path);
    if (needsAuth && options?.checkAuth !== false) {
      // Check if user is authenticated
      if (!options?.currentUser?.uid) {
        // Deep link authentication required
        // Return path but mark as requiring auth - let the page handle redirect
        return { 
          path: '/login', 
          params: new URLSearchParams([['redirect', path]]),
          requiresAuth: true 
        };
      }
    }

    // SECURITY: Extract and validate resource ID if present
    const resourceId = extractResourceId(path);
    if (resourceId) {
      // SECURITY: For order IDs, validate format to prevent IDOR via malformed IDs
      // Note: Full ownership verification happens in the page component (getOrderById)
      // This prevents navigation to invalid order IDs, but ownership is checked server-side
      if (resourceId.type === 'order') {
        // SECURITY: Validate order ID format (Firestore document IDs: 10-128 chars, alphanumeric + _-)
        const orderIdRegex = /^[a-zA-Z0-9_-]{10,128}$/;
        if (!orderIdRegex.test(resourceId.id)) {
          // Invalid order ID format - redirect to home to prevent IDOR attempts
          return { path: '/', params: new URLSearchParams() };
        }
      }
    }

    // SECURITY: Sanitize and validate query parameters
    // SECURITY: DoS Protection - Limit number of query parameters
    const params = new URLSearchParams();
    let paramCount = 0;
    const MAX_PARAMS = 20; // Prevent DoS via excessive query parameters
    
    for (const [key, value] of urlObj.searchParams.entries()) {
      // SECURITY: DoS Protection - Limit parameter count
      if (paramCount >= MAX_PARAMS) {
        break; // Stop processing if too many parameters
      }
      
      const sanitizedKey = sanitizeString(key, 100);
      const sanitizedValue = sanitizeString(value, 500);
      
      // SECURITY: Validate parameter key and value are not empty after sanitization
      if (sanitizedKey && sanitizedValue && sanitizedKey.length > 0 && sanitizedValue.length > 0) {
        params.append(sanitizedKey, sanitizedValue);
        paramCount++;
      }
    }
    
    return { 
      path, 
      params,
      requiresAuth: needsAuth,
      resourceId 
    };
  } catch (error) {
    // SECURITY: Removed console.error to prevent information leakage
    return { path: '/', params: new URLSearchParams() };
  }
}

/**
 * Check if current page was opened via deep link
 */
export function isDeepLinkOpen(): boolean {
  // Check if opened via app (PWA or native)
  if (isAppInstalled()) {
    return true;
  }
  
  // Check URL parameters that indicate deep link
  const params = new URLSearchParams(window.location.search);
  return params.has('deep_link') || params.has('app_open');
}

/**
 * SECURITY: Smart link handler with comprehensive validation
 * Opens app if installed, otherwise opens web
 * Use this for links in emails, SMS, etc.
 * For Android: Uses Intent URLs for better app opening
 */
export function smartLinkHandler(
  webUrl: string,
  appScheme?: string,
  onAppOpen?: () => void,
  onWebOpen?: () => void
): void {
  // SECURITY: Validate and sanitize URL first
  const validation = validateAndSanitizeUrl(webUrl);
  if (!validation.valid || !validation.sanitized) {
    // SECURITY: Removed console.error to prevent information leakage
    onWebOpen?.();
    return;
  }

  const safeUrl = validation.sanitized;
  const scheme = appScheme || ANDROID_APP_SCHEME;
  
  // SECURITY: Validate scheme
  if (appScheme && !validateUrlScheme(appScheme)) {
    // SECURITY: Removed console.error to prevent information leakage
    window.location.href = safeUrl;
    onWebOpen?.();
    return;
  }

  // SECURITY: Rate limiting
  const rateLimitKey = `smartlink_${window.location.hostname}`;
  const rateLimit = checkRateLimit(rateLimitKey);
  if (!rateLimit.allowed) {
    // SECURITY: Removed console.warn to prevent information leakage
  }

  if (isAppInstalled()) {
    // Already in app (PWA), just navigate
    window.location.href = safeUrl;
    onAppOpen?.();
    return;
  }
  
  // Try to open native app
  if (isAndroid()) {
    try {
      // SECURITY: Validate package name
      if (!validatePackageName(ANDROID_PACKAGE_NAME)) {
        // SECURITY: Removed console.error to prevent information leakage
        window.location.href = safeUrl;
        onWebOpen?.();
        return;
      }

      const urlObj = new URL(safeUrl);
      const path = urlObj.pathname + urlObj.search;
      
      // SECURITY: Sanitize components
      const sanitizedHost = sanitizeString(urlObj.hostname, 253) || urlObj.hostname;
      const sanitizedPath = sanitizeString(path, 2000) || path;
      
      const intentUrl = `intent://${sanitizedHost}${sanitizedPath}#Intent;scheme=https;package=${ANDROID_PACKAGE_NAME};S.browser_fallback_url=${encodeURIComponent(safeUrl)};end`;
      
      window.location.href = intentUrl;
      onAppOpen?.();
      
      // Fallback to web if app doesn't open
      setTimeout(() => {
        if (document.visibilityState === 'visible') {
          window.location.href = safeUrl;
          onWebOpen?.();
        }
      }, 2000);
    } catch (error) {
      // SECURITY: Removed console.error to prevent information leakage
      window.location.href = safeUrl;
      onWebOpen?.();
    }
  } else {
    // iOS or other: Use custom scheme
    try {
      const urlObj = new URL(safeUrl);
      const path = urlObj.pathname + urlObj.search;
      const sanitizedPath = sanitizeString(path, 2000) || path;
      const appUrl = `${scheme}${sanitizedPath}`;
      
      window.location.href = appUrl;
      onAppOpen?.();
      
      // Fallback to web
      setTimeout(() => {
        if (document.visibilityState === 'visible') {
          window.location.href = safeUrl;
          onWebOpen?.();
        }
      }, 2000);
    } catch (error) {
      // SECURITY: Removed console.error to prevent information leakage
      window.location.href = safeUrl;
      onWebOpen?.();
    }
  }
}

/**
 * SECURITY: Get Android Intent URL for deep linking with validation
 * Use this to generate links that will open the Android app
 * All inputs are validated and sanitized
 */
export function getAndroidIntentUrl(webUrl: string): string {
  // SECURITY: Validate and sanitize URL first
  const validation = validateAndSanitizeUrl(webUrl);
  if (!validation.valid || !validation.sanitized) {
    // SECURITY: Removed console.error to prevent information leakage
    // Return safe fallback
    return validation.sanitized || webUrl;
  }

  const safeUrl = validation.sanitized;

  try {
    // SECURITY: Validate package name
    if (!validatePackageName(ANDROID_PACKAGE_NAME)) {
      // SECURITY: Removed console.error to prevent information leakage
      return safeUrl;
    }

    const urlObj = new URL(safeUrl);
    const path = urlObj.pathname + urlObj.search;
    
    // SECURITY: Sanitize components
    const sanitizedHost = sanitizeString(urlObj.hostname, 253) || urlObj.hostname;
    const sanitizedPath = sanitizeString(path, 2000) || path;
    
    return `intent://${sanitizedHost}${sanitizedPath}#Intent;scheme=https;package=${ANDROID_PACKAGE_NAME};S.browser_fallback_url=${encodeURIComponent(safeUrl)};end`;
  } catch (error) {
    // SECURITY: Removed console.error to prevent information leakage
    return safeUrl;
  }
}

